import { useQuery } from '@apollo/client';
import {
  Toolbar,
  FluentProvider,
  webLightTheme,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { INavLink, INavLinkGroup } from '@m365-admin/nav';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import hasAccess from '../../utils/authorization/authorizationCheck';
import UserContext from '../../utils/authorization/UserContext';
import { GET_REPORTS, IReport } from '../../utils/powerBiApi/powerBiApi';
import { StatUser } from '../../utils/statApi/UsersApi';
import LoadingErrorMessage from '../common/errorContent/LoadingErrorMessage';
import FullWidthHeader from '../common/headers/FullWidthHeader';
import MainDetailsView from '../common/mainDetail/MainDetailsView';
import { IPowerBIReportIdParams } from '../common/ParamTypes';

// Lazy-loaded so the PowerBI client (powerbi-client / -react / -models) is code-split
// into its own chunk and only downloaded when a report is actually opened.
const EmbeddedReport = React.lazy(() => import('./EmbeddedReport'));

const useStyles = makeStyles({
  toolbar: {
    padding: '0',
    background: tokens.colorNeutralBackground1,
  },
});

const ReportsListPage = (): JSX.Element => {
  const styles = useStyles();
  const history = useHistory();

  const { reportId } = useParams<IPowerBIReportIdParams>();
  const [currentReportId, setCurrentReportId] = useState<string>();

  const { data, loading, error } = useQuery(GET_REPORTS);
  const reportsList: IReport[] = data?.reports;
  const baseUrl = '/reports-dashboard';
  const user: StatUser = React.useContext(UserContext);
  const hasExpediteAccess = hasAccess(user.permissions, 'Reports:ViewExpediteReports');

  const visibleReports = useMemo(() => {
    const hiddenReportNames = ['Stat Contacts Report'] as string[];
    if (!hasExpediteAccess) {
      hiddenReportNames.push('Expedite Power App');
    }
    return reportsList?.filter((report) => !hiddenReportNames.includes(report.name));
  }, [reportsList, hasExpediteAccess]);

  useEffect(() => {
    if (visibleReports && visibleReports.length > 0) {
      let currentReportForUri: IReport = null;
      if (reportId) {
        currentReportForUri = visibleReports.find((rpt) => rpt.reportUri === reportId);
      }
      if (!reportId || !currentReportForUri) {
        history.replace(`${baseUrl}/${visibleReports[0].reportUri}`);
        setCurrentReportId(visibleReports[0].id);
      } else {
        setCurrentReportId(currentReportForUri.id);
      }
    }
  }, [visibleReports, reportId]);

  const getNavLinkGroups = (): INavLinkGroup[] => {
    const reportNavLinks: INavLink[] =
      visibleReports?.map(
        (report: { id: string; name: string; reportUri: string }, index: number) => {
          return {
            key: report.id,
            name: report.name,
            ariaLabel: report.name,
            url: report.reportUri,
            isSelected: report.reportUri === reportId,
            onClick: (ev: React.MouseEvent<HTMLElement>, item: INavLink): void => {
              ev.preventDefault();
              ev.stopPropagation();
              setCurrentReportId(report.id);
              history.push(`${baseUrl}/${report.reportUri}`);
            },
          } as INavLink;
        },
      ) || [];

    return [{ key: 'reports', links: reportNavLinks }];
  };
  const getHeaderTitle = (): JSX.Element => {
    return <h1>Reports</h1>;
  };
  const getNavSectionHeader = (): JSX.Element => {
    return (
      <FluentProvider theme={webLightTheme}>
        <Toolbar
          className={styles.toolbar}
          aria-label="Use left and right arrow keys to navigate between commands"
        />
      </FluentProvider>
    );
  };

  return (
    <>
      <FullWidthHeader title={getHeaderTitle} />
      <LoadingErrorMessage error={error} loading={loading} />
      <MainDetailsView
        navLinkGroups={getNavLinkGroups()}
        navSectionHeader={getNavSectionHeader()}
        navAriaLabel="Reports navigation"
      >
        {currentReportId && (
          <React.Suspense
            fallback={
              <LoadingErrorMessage
                loading
                error={undefined}
                actionName="loading the report from Power Bi"
              />
            }
          >
            <EmbeddedReport reportId={currentReportId} />
          </React.Suspense>
        )}
      </MainDetailsView>
    </>
  );
};

export default ReportsListPage;
